import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Bug, ExternalLink } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

function getDeviceType(): string {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [includeActivity, setIncludeActivity] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [createdThreadId, setCreatedThreadId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!description.trim() || description.trim().length < 5) {
      toast({ title: "Too short", description: "Please describe the issue in more detail.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const metadata: Record<string, any> = {
        pageUrl: window.location.href,
        browserInfo: navigator.userAgent,
        deviceType: getDeviceType(),
        screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      };

      if (includeActivity) {
        try {
          const recentLogs = sessionStorage.getItem('activityLogs');
          if (recentLogs) {
            metadata.activitySnapshot = JSON.parse(recentLogs);
          }
        } catch {}
      }

      const payload = {
        subject: description.trim().slice(0, 100),
        content: description.trim(),
        tag: 'bug',
        priority: 'high',
        metadata,
      };

      const res = await fetchWithAuth('/api/support/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setCreatedThreadId(data.thread?.id || data.id || null);
        toast({ title: "Bug report submitted", description: "Thank you! We'll look into it." });
        setDescription('');
        setIncludeActivity(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast({ title: "Failed to submit", description: err.message || "Please try again.", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCreatedThreadId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2 text-sm">
          <Bug className="w-4 h-4" />
          Report a Bug
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5" />
            Report a Bug
          </DialogTitle>
        </DialogHeader>

        {createdThreadId ? (
          <div className="space-y-4 text-center py-4">
            <div className="text-green-600 text-lg font-semibold">Bug report submitted!</div>
            <p className="text-sm text-gray-600">
              Your report has been received and our team will review it shortly.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                variant="default"
                className="gap-2"
                onClick={() => {
                  handleClose(false);
                  window.location.href = `/support-inbox`;
                }}
              >
                <ExternalLink className="w-4 h-4" />
                View Support Inbox
              </Button>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Describe what happened</label>
              <Textarea
                placeholder="What went wrong? What did you expect to happen?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="resize-none"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="include-activity"
                checked={includeActivity}
                onCheckedChange={(checked) => setIncludeActivity(checked === true)}
              />
              <label htmlFor="include-activity" className="text-sm text-gray-600 cursor-pointer">
                Include recent activity log
              </label>
            </div>

            <p className="text-xs text-gray-400">
              Your device info and current page will be included automatically to help us reproduce the issue.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || description.trim().length < 5}>
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
